/**
 * Server-injected bootstrap data. The Express handler renders
 *   <script>window.__ORI_BOOTSTRAP__ = {...}</script>
 * before the SPA bundle so the client can pick a page without a fetch.
 *
 * Each route variant carries the data shape that PageComponent expects.
 */

import type { EmberSsrData } from '../modules/ember/api';

export type BootstrapRoute =
  | {
      route: 'owner-console';
      /** True when the request carried a valid session cookie. When false the
       *  server redirects to the door; the SPA's own bounce is a backstop. */
      authed?: boolean;
      /** The user's assigned Ember number (lane reply_route.linq_from_number),
       *  baked in so the tab-bar "ask Ember" button can sms: straight to it. */
      oriNumber?: string;
    }
  | {
      /** The notes the owner told / was sent (v0.6 §21). Same auth posture
       *  as owner-console: the server redirects signed-out to the door. */
      route: 'my-notes';
      authed?: boolean;
    }
  | {
      /**
       * Ember v1.2 rebuilt product SPA (新设计文档 §2/§4). One bootstrap route
       * for every public page — the client router reads location.pathname
       * ('/', '/requests', '/talk', '/result/:id', '/me/…'). Auth is soft:
       * public pages render anonymous; gated pages redirect to /auth.
       */
      route: 'ember';
      authed: boolean;
      actorId?: string | null;
      selfName?: string | null;
      /** Path the server rendered this shell for (informational; the router reads location). */
      path?: string;
      /** Stable homepage copy rendered into `/` before JavaScript starts. */
      home?: {
        hero: {
          headline: string;
          supporting_text: string;
          composer_placeholder: string;
          composer_placeholder_return: string;
          trust_line: string;
          why_ember: { eyebrow: string; text: string; differences: { label: string; text: string; shot: string }[] };
          how_it_works: {
            sub: string;
            always: { label: string; steps: { who: string; text: string; gate?: string; shot: string }[] };
            sometimes: { label: string; sub: string; steps: { who: string; text: string; gate?: string; shot: string }[] };
            foot: string;
          };
        };
      };
      /** Public route data already rendered into the initial HTML. */
      ssr?: EmberSsrData;
      /**
       * Firebase web-SDK config for the /auth OTP door. Present ONLY when the
       * edge runs the Firebase code channel — its presence is the page's
       * single signal that a sign-in code can be requested. Every value is a
       * public identifier (they name the project, they authorize nothing);
       * injected per request so a project swap needs no SPA rebuild.
       */
    }
  | { route: 'gmail-auth'; state?: string }
  | { route: 'calendar-auth'; state?: string }
  | {
      route: 'legal-review';
      /** The version the server considers current at render time. */
      currentVersion: string;
      /**
       * The version the inbound link was minted with — usually equal to
       * currentVersion, but if the env's CURRENT_LEGAL_VERSION moved
       * between link-mint and click the page can detect the drift and
       * refuse to let the user accept a stale version.
       */
      requestedVersion?: string;
      /** Which documents changed in this version. Controls which sections render. */
      docsUpdated?: { terms?: boolean; privacy?: boolean };
      /** Optional plain-English summary of what changed. Empty string when blank. */
      versionNotes?: string;
      /**
       * Ember number this user's gated thread is on — baked into the review
       * link by the tos-outdated gate. Powers the post-accept "Return to
       * Messages" sms: deep link straight back to that thread. Empty when
       * unknown (page falls back to text-only "head back" copy).
       */
      oriNumber?: string;
      /**
       * Same-origin path to return to after accepting — how the web product
       * puts someone back on the page the gate interrupted. The messaging flow
       * uses `oriNumber` instead (an sms: link back to the thread); a given
       * visit has one or the other, never both. Server-sanitized.
       */
      returnTo?: string;
    }
  | {
      route: 'checkout';
      /** Active plan products (wire config); display copy is formatted client
       *  side. Baked in so the page renders without a fetch. */
      plans?: CheckoutPlan[];
      /** Ember number this person's gated thread is on, for the "back to
       *  Messages" link after subscribing. */
      oriNumber?: string;
    }
  | {
      route: 'checkout-success';
      /** Stripe Checkout Session id from the success redirect (?session_id). */
      sessionId?: string;
      oriNumber?: string;
    }
  | { route: 'unknown' };

/** Public plan shape returned by /api/billing/plans and baked into bootstrap. */
export interface CheckoutPlan {
  plan_key: string;
  interval: string;
  interval_count: number;
  price_amount: number | null;
  price_currency: string;
}

export type Bootstrap = BootstrapRoute & {
  /**
   * Short commit of the deploy that rendered this shell. The stale-bundle
   * watcher compares it against /__version when the tab returns to the
   * foreground and reloads on mismatch. Absent in local dev without git
   * metadata.
   */
  appVersion?: string;
  /**
   * Analytics protocol carried by server-rendered pages. `page_id` names this
   * one document load; `source_page_id` is the document that linked to it;
   * `attempt_id` groups one visitor's retries at whatever this page asks of them.
   */
  analytics_version?: 2;
  page_id?: string;
  source_page_id?: string;
  entry_intent?: 'signup' | 'signin';
  attempt_id?: string;
  /** Optional flag to render Owner mode by default (admin preview links). */
  ownerView?: boolean;
  /**
   * Optional session pre-mint injected by the server right after
   * onboarding/plan-signup submit. When present, SessionProvider
   * uses these instead of the cached value on first paint.
   */
  sessionToken?: string;
  actorId?: string;
  selfName?: string;
  /**
   * Google Maps JS API key for production map rendering. When present,
   * RouteMap auto-selects the Google provider; otherwise it falls back
   * to Leaflet + OSM (dev default).
   */
  googleMapsApiKey?: string;
  /** Optional Map ID (Cloud-based styling). Required for AdvancedMarker. */
  googleMapId?: string;
};

declare global {
  interface Window {
    __ORI_BOOTSTRAP__?: Bootstrap;
  }
}

export function readBootstrap(): Bootstrap {
  if (typeof window === 'undefined') return { route: 'unknown' };
  return window.__ORI_BOOTSTRAP__ ?? { route: 'unknown' };
}
